import { ImageName } from "@mcleod/images";
import { AutogenLayoutDatControl } from "./autogen/AutogenLayoutDatControl";
import {
    BlurEvent, Button,
    ChangeEvent,
    ClickEvent,
    Component,
    DataDisplayEvent,
    DataSourceMode, DropdownItem, Label, Layout,
    LayoutProps, LookupModelSearchEvent, Snackbar, Switch, TableRow, TableRowCreationEvent,
    TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import {UpdateCredentialsDialog} from "@mcleod/general/src/UpdateCredentialsDialog";

interface controlLayoutProps extends LayoutProps {
    vendorId: string;
}

export class DatControl extends AutogenLayoutDatControl {

    private _vendorId: string;
    private _isTokenValid: boolean;
    private _orgAuthErrorMessage: string;
    private _orgAuthAttempted: boolean;
    private originalDatAccountId: string;
    private originalDatPassword: string;
    private shouldUpdateLoadboardCredentials: boolean = false;
    private promptTriggered: boolean;
    private hasDatLoadboardCredentials: boolean = false;

    private _timeFrameContractOptions: DropdownItem[] = [
        new DropdownItem("30D", "30 Days"),
        new DropdownItem("60D", "60 Days"),
        new DropdownItem("90D", "90 Days"),
        new DropdownItem("365D", "365 Days")
    ];

    private _timeFrameSpotOptions: DropdownItem[] = [
        new DropdownItem("3D", "3 Days"),
        new DropdownItem("7D", "7 Days"),
        new DropdownItem("15D", "15 Days"),
        new DropdownItem("30D", "30 Days"),
        new DropdownItem("60D", "60 Days"),
        new DropdownItem("90D", "90 Days"),
        new DropdownItem("365D", "365 Days")
    ];

    constructor(props?: Partial<controlLayoutProps>) {
        super(props);
        this._vendorId = props?.vendorId;
    }

    async onLoad() {
        this.mainDataSource.addAfterModeChangeListener(event => {
            if (event.newMode === DataSourceMode.ADD) {
                if (this.mainDataSource.activeRow == null) {
                    return;
                }
                this.mainDataSource.activeRow._appending = false;
                this.activeRow.setValues(this.activeRow?.get("dat_default_values"));

            }
        });

        this.sourceMarketrateExprt.addAfterModeChangeListener(event => {
            if (event.newMode === DataSourceMode.ADD) {
                if (this.sourceMarketrateExprt.activeRow == null) {
                    return;
                }
                this.sourceMarketrateExprt.activeRow._appending = false;
                this.sourceMarketrateExprt.activeRow.setValues(this.sourceMarketrateExprt.activeRow?.get("dat_default_values"));
            }
        });
        await this.sourceLmeUserToken.search({ is_rateview: "Y" });
        this.sourcePbwLoadboardServices.search({"service_type": "T"}).then(result => {
            if(result && result.modelRows.length > 0){
                this.hasDatLoadboardCredentials = true;
            }
        });
    }

    panelRateViewOnDataDisplay(event: DataDisplayEvent) {
        this.panelDatDefault.visible = !this.activeRow?.get("dat_rateview_enabled");
        this.panelRateView.visible = this.activeRow?.get("dat_rateview_enabled");
    }

    async postDataSources(vendorId: string): Promise<boolean> {
        try {
            if (!this.validateRequiredFields(this.mainDataSource?.activeRow)) {
                if (this.mainDataSource.mode == DataSourceMode.ADD) {
                    this.mainDataSource.activeRow._appending = true;
                }

                await this.mainDataSource.post();
                return false;
            }

            this.textboxUserName.required = this.switchEnableMarketRate.checked;
            this.textboxMarketRatePassword.required = this.switchEnableMarketRate.checked;
            this.sourceMarketrateExprt?.activeRow?.set("vendor", "T");
            if (!this.sourceMarketrateExprt?.activeRow?.get("account_number")) {
                this.sourceMarketrateExprt?.activeRow?.set("account_number", "2");
            }
            if (this.sourceMarketrateExprt.mode == DataSourceMode.ADD) {
                this.sourceMarketrateExprt.activeRow._appending = true;
            }
            await this.sourceMarketrateExprt.post();

            this.mainDataSource?.activeRow?.set("vendor", vendorId);
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                this.mainDataSource.activeRow._appending = true;
            }

            if(this.shouldUpdateLoadboardCredentials){
                await this.sourcePbwLoadboardServices.search({"service_type": "T"});
                this.sourcePbwLoadboardServices.activeRow.set("user_id", this.mainDataSource.activeRow.get("dat_account_id"));
                this.sourcePbwLoadboardServices.activeRow.set("password", this.mainDataSource.activeRow.get("dat_password"));
                this.sourcePbwLoadboardServices.post();

            }

            await this.mainDataSource.post();
            return true;
        } catch (error) {
            return false;
        }
    }


    switchUseMarketRateFuelSurchargeOnChange(event: ChangeEvent) {
        this.textboxCustomerId.visible = event.newValue === false;
        this.textboxCustomerId.required = event.newValue === false;
    }

    switchEnableMarketRateExportOnChange(event: ChangeEvent) {
        if (event.newValue) {
            this.setMarketRateExportVisibility(true);
            this.textboxUserName.required = true;
            this.textboxMarketRatePassword.required = true;
        } else {
            this.textboxUserName.required = false;
            this.textboxMarketRatePassword.required = false;
            this.setMarketRateExportVisibility(false);
        }
        this.marketRateExportSection.visible = event.newValue;
    }

    validateFieldsOnBlur(event: BlurEvent) {
       if(event.changedWhileFocused && this.hasValidFieldsForDatRest(this.mainDataSource.activeRow)){
                // prompt the user to update credentials
           if(this.hasDatLoadboardCredentials && !this.promptTriggered) {
               this.promptUserToUpdateCredentials();
           }else{
               this.buttonOrganizationAuth.visible = true;
               this.buttonOrganizationAuth.enabled = true;
           }

        }
    }

    textboxDatAuthFieldsOnFocus(event: FocusEvent) {
        this.originalDatAccountId = this.mainDataSource.activeRow?.originalData?.dat_account_id;
        this.originalDatPassword = this.mainDataSource.activeRow?.originalData?.dat_password;
    }


    // Standalone function to set required fields
    setRequiredPanel(panel: any, required: boolean) {
        if (!panel || !panel._components) return;

        panel._components.forEach((component: any) => {
            if (component instanceof Textbox) {
                component.required = required;
            }
        });
    }

    async validateFieldsOnDataDisplay(event: DataDisplayEvent): Promise<void> {
        const activeRow = this.mainDataSource?.activeRow;
        const isRateViewEnabled = activeRow?.get("dat_rateview_enabled") === true || activeRow?.data?.dat_default_values?.dat_rateview_enabled === true;
        let orgAuthErrorMessage: string;
        let orgAuthAttempted: boolean;

        if (isRateViewEnabled) {
            const hasAllRequiredFields = !StringUtil.isEmptyString(activeRow?.get("dat_account_id")) &&
                !StringUtil.isEmptyString(activeRow?.get("dat_password")) &&
                !StringUtil.isEmptyString(activeRow?.get("rateview_auth_url"));
            const isTokenValid = (activeRow?.get("is_token_valid") === true || this._isTokenValid === true) && hasAllRequiredFields;

            const result = await this.sourceOrganizationToken.search({ vendor: "T" });
            const rowOrgToken = result?.modelRows[0];
            orgAuthErrorMessage = !StringUtil.isEmptyString(rowOrgToken?.get("dat_org_auth_error")) ?
                rowOrgToken?.get("dat_org_auth_error") :
                this._orgAuthErrorMessage;
            orgAuthAttempted = rowOrgToken?.get("authorization_attempted") === true || this._orgAuthAttempted === true;

            this.tableRateViewUsers.visible = isTokenValid && isRateViewEnabled;
            this.labelAssociatedUsers.visible = isTokenValid && isRateViewEnabled;
            this.panelDatDefault.visible = false;
            this.panelRateView.visible = true;
            this.setRequiredPanel(this.panelRateView, true);
            this.setRequiredPanel(this.panelDatDefault, false);
            this.labelAuthStatus.visible = hasAllRequiredFields && (orgAuthAttempted || isTokenValid);
            this.setupAuthorizationLabel(isTokenValid, orgAuthErrorMessage, orgAuthAttempted);
            this.buttonOrganizationAuth.visible = hasAllRequiredFields && !isTokenValid;
            this.buttonOrganizationAuth.enabled = this.hasRequiredRateViewDataFields();
            this.setupButtonOrganizationAuthTooltip();
        } else {
            this.panelDatDefault.visible = true;
            this.panelRateView.visible = false;
            this.setRequiredPanel(this.panelRateView, false);
            this.setRequiredPanel(this.panelDatDefault, true);
        }
    }


    async buttonAuthorizeOnClick(event: ClickEvent) {
        if(this.hasDatLoadboardCredentials && !this.promptTriggered) {
            const continueWithUpdate = this.promptUserToUpdateCredentials();
            if(!continueWithUpdate) {
                return;
            }
        }


        const response = await Api.post("lme/general/rateindex/rateview/auth", {
            "action_type": "O",
            "account_id": this.mainDataSource.activeRow.get("dat_account_id"),
            "dat_password": this.mainDataSource.activeRow.get("dat_password"),
            "rateview_auth_url": this.mainDataSource.activeRow.get("rateview_auth_url")
        })
        if (response) {
            // reset label / button
            if (StringUtil.equalsIgnoreCase(response.data[0].dat_org_auth_error, "Authentication is blocked for one minute.")) {
                Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
            }
            this._orgAuthAttempted = true;
            this.setupAuthorizationLabel(response.data[0].is_valid_token, response.data[0].dat_org_auth_error, true);
            this.buttonOrganizationAuth.visible = !response.data[0].is_valid_token;
            this.tableRateViewUsers.visible = response.data[0].is_valid_token;
            this.labelAssociatedUsers.visible = response.data[0].is_valid_token;
            this._isTokenValid = response.data[0].is_valid_token;
            this._orgAuthErrorMessage = response.data[0].dat_org_auth_error;
            this.mainDataSource.mode = DataSourceMode.UPDATE;
        }
    }

    tableRateViewUsersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow: ModelRow = tableRow.data;
        const isUserTokenValid = modelRow.get("is_valid_token", false);
        const errorMessage = modelRow.get("user_auth_error");
        const textboxUserId = tableRow.findComponentById("textboxDatRateviewUserId") as Textbox;
        if (modelRow._appending) {
            if (tableRow !== this.tableRateViewUsers.quickAddRow) {
                textboxUserId.text = modelRow.get("user_id");
                this.setupUserAuthorizationLabel(isUserTokenValid, tableRow, errorMessage);
            }
        } else {
            textboxUserId.text = modelRow.get("user_id");
            this.setupUserAuthorizationLabel(isUserTokenValid, tableRow, errorMessage);
        }
    }

    tableRateViewUsersOnRowCreate(event: TableRowCreationEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data;
        if (modelRow._appending) {
            if (this.tableRateViewUsers.quickAddRow !== tableRow) {
                Api.post("lme/general/rateindex/rateview/auth", {
                    "action_type": "U",
                    "user_id": modelRow.get("user_id"),
                    "dat_rateview_user": modelRow.get("dat_user_id")
                }).then(response => {
                    if (response?.data[0]?.is_valid_token) {
                        modelRow.set("is_valid_token", true);
                        this.setUserAuthSuccess(tableRow)
                    } else {
                        if (StringUtil.equalsIgnoreCase(response.data[0].dat_user_auth_error, "Authentication is blocked for one minute.")) {
                            Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
                        }
                        this.setUserAuthFailed(tableRow, response?.data[0].dat_user_auth_error);
                        modelRow.set("is_valid_token", false);
                    }
                });
            }
        }
    }

    textboxDatUserOnBlur(event: BlurEvent) {
        const textboxDatUser = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(event.target as Textbox);

        if (this.tableRateViewUsers.quickAddRow !== tableRow) {
            if (StringUtil.isEmptyString(textboxDatUser.text)) {
                textboxDatUser.text = tableRow.data?.originalData?.dat_user_id;
                return;
            }
            const textboxUserId = tableRow.findComponentById("textboxDatRateviewUserId") as Textbox;
            const labelRateViewUserStatus = tableRow.findComponentById("labelRateViewUserStatus") as Label;
            Api.post("lme/general/rateindex/rateview/auth", {
                "action_type": "U",
                "user_id": textboxUserId.text,
                "dat_rateview_user": textboxDatUser.text
            }).then(response => {
                labelRateViewUserStatus.visible = true;
                if (response?.data[0]?.is_valid_token) {
                    this.setUserAuthSuccess(tableRow)
                } else {
                    labelRateViewUserStatus.imageName = ImageName.circleX;
                    labelRateViewUserStatus.imageColor = "error";
                    labelRateViewUserStatus.tooltip = response?.data[0]?.dat_user_auth_error;
                    if (StringUtil.equalsIgnoreCase(response.data[0].dat_user_auth_error, "Authentication is blocked for one minute.")) {
                        Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
                    }

                }
            });
        }
    }

    setMarketRateExportVisibility(isVisible: boolean) {
        this.marketRateExportSection.visible = isVisible;
        this.textboxAccountNum.required = isVisible;
        this.textboxSftpUrl.required = isVisible;
        this.textboxCompanyType.required = isVisible;
        this.textboxLagDays.required = isVisible;
        this.textboxProcessDays.required = isVisible;
        this.textboxShipperName.required = isVisible;
        this.textboxCarrierName.required = isVisible;
        this.textboxCustomerName.required = isVisible;
        this.textboxConsigneeName.required = isVisible;

    }

    private validateRequiredFields(activeRow: ModelRow | undefined) {
        if (activeRow?.data?.dat_rateview_enabled) {
            return activeRow?.get("dat_account_id") && activeRow?.get("dat_password");
        } else {
            return activeRow?.get("user_name") && activeRow?.get("password") && activeRow?.get("webservice_address");
        }
    }

    private hasRequiredRateViewFields() {
        return !StringUtil.isEmptyString(this.textboxDatAccountId.text)
            && !StringUtil.isEmptyString(this.textboxDatPassword.text);
    }
    private hasRequiredRateViewDataFields() {
        return !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("dat_account_id"))
            && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("dat_password"));
    }

    private setupAuthorizationLabel(tokenValid: boolean, datOrgAuthError: string, orgAuthAttempted: boolean) {
        if (tokenValid) {
            this.labelAuthStatus.imageName = ImageName.circleCheck;
            this.labelAuthStatus.imageColor = "success";
            this.labelAuthStatus.tooltip = "Organization Token is successfully authorized.";
        } else {
            this.labelAuthStatus.imageName = ImageName.circleX;
            this.labelAuthStatus.imageColor = "error";
            if (!StringUtil.isEmptyString(datOrgAuthError)) {
                this.labelAuthStatus.tooltip = datOrgAuthError;
            }
        }
        this.labelAuthStatus.visible = orgAuthAttempted || tokenValid;
    }

    private setupButtonOrganizationAuthTooltip() {
        if (!this.buttonOrganizationAuth.enabled) {
            this.buttonOrganizationAuth.tooltip = "Please provide all required fields";
        } else {
            this.buttonOrganizationAuth.tooltip = null;
        }
    }

    private setUserAuthSuccess(tableRow: TableRow) {
        const textboxDatRateviewUserId = tableRow.findComponentById("textboxDatRateviewUserId") as Textbox;
        const labelDatStatus = tableRow.findComponentById("labelRateViewUserStatus") as Label;
        const buttonAuthorizeUser = tableRow.findComponentById("buttonAuthorizeUser") as Label;
        labelDatStatus.imageName = ImageName.circleCheck;
        labelDatStatus.imageColor = "success";
        labelDatStatus.tooltip = "User is successfully authenticated.";
        textboxDatRateviewUserId.enabled = false;
        buttonAuthorizeUser.visible = false;
    }

    private setUserAuthFailed(tableRow: TableRow, errorMessage: string) {
        const textboxDatRateviewUserId = tableRow.findComponentById("textboxDatRateviewUserId") as Textbox;
        const labelDatStatus = tableRow.findComponentById("labelRateViewUserStatus") as Label;
        const buttonAuthorizeUser = tableRow.findComponentById("buttonAuthorizeUser") as Label;
        labelDatStatus.imageName = ImageName.circleX;
        labelDatStatus.imageColor = "error";
        labelDatStatus.tooltip = errorMessage;
        textboxDatRateviewUserId.enabled = false;
        buttonAuthorizeUser.visible = true;
    }

    private setupUserAuthorizationLabel(isUserTokenValid: boolean, tableRow: TableRow, errorMessage: any) {
        if (isUserTokenValid) {
            this.setUserAuthSuccess(tableRow);
        } else {
            this.setUserAuthFailed(tableRow, errorMessage);
        }
    }

    private switchEnabledTypeSelectionOnDataDisplay(event: DataDisplayEvent) {
        const activeRow = this.mainDataSource?.activeRow;
        const isRateViewEnabled = activeRow?.get("dat_rateview_enabled") === true || activeRow?.data?.dat_default_values?.dat_rateview_enabled === true;
        this.switchEnabledTypeSelection.visible = isRateViewEnabled;
        if (isRateViewEnabled) {
            this.textboxEscalationType.visible = this.switchEnabledTypeSelection.checked;
            this.textboxEscalationType.required = this.switchEnabledTypeSelection.checked;
        }
    }

    private switchEnabledTypeSelectionOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const switchEnabledTypeSelection = event.target as Switch;
            if (switchEnabledTypeSelection.checked) {
                const activeRow = this.mainDataSource?.activeRow;
                const escalationType = activeRow?.get("escalation_type");
                this.textboxEscalationType.visible = true;
                this.textboxEscalationType.required = true;
                this.updateEscalationFieldsVisibility(escalationType);
            } else {
                this.hideAndDisable(this.textboxEscalationType);
                this.hideAndDisable(this.textboxSpecificTimeFrame);
                this.hideAndDisable(this.textboxSpecificTimeframeContract)
                this.hideAndDisable(this.textboxSpecificAreaType);
            }
        }
    }

    private hideAndDisable(textbox: Textbox) {
        textbox.visible = false;
        textbox.required = false;
    }


    private textboxEscalationTypeOnDataDisplay(event: DataDisplayEvent) {
        const activeRow = this.mainDataSource?.activeRow;
        const escalationType = activeRow?.get("escalation_type");
        this.updateEscalationFieldsVisibility(escalationType);
    }

    private textboxEscalationTypeOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textboxEscalationType = event.target as Textbox;
            const escalationType = textboxEscalationType?.selectedItem?.value;
            this.updateEscalationFieldsVisibility(escalationType);
        }
    }

    private switchRatesOnDataDisplay(event: DataDisplayEvent) {
        this.updateEscalationComboValues();
    }

    private switchSpotRatesOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.updateEscalationComboValues();
            const escalationType = this.mainDataSource.activeRow?.get("escalation_type");
            this.updateEscalationFieldsVisibility(escalationType);
        }
    }

    private switchContractRatesOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.updateEscalationComboValues();
            const escalationType = this.mainDataSource.activeRow?.get("escalation_type");
            this.updateEscalationFieldsVisibility(escalationType);
        }
    }


    private updateEscalationFieldsVisibility(escalationType: string) {
        if (!StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("enabled_escalation_option")) && StringUtil.equalsIgnoreCase(this.mainDataSource.activeRow?.get("enabled_escalation_option"), "Y")) {
            if (!StringUtil.isEmptyString(escalationType)) {
                const isSpecificTimeFrameVisible = ["S", "M", "SA"].some(type =>
                    StringUtil.equalsIgnoreCase(type, escalationType)
                );
                const isSpecificAreaTypeVisible = ["S", "M", "SS"].some(type =>
                    StringUtil.equalsIgnoreCase(type, escalationType)
                );
                const isSpotRatesChecked = this.mainDataSource.activeRow?.get("spot_rate_type");
                const isContractRatesChecked = this.mainDataSource.activeRow?.get("contract_rate_type");

                this.textboxSpecificTimeFrame.visible = isSpecificTimeFrameVisible && isSpotRatesChecked === "Y";
                this.textboxSpecificTimeFrame.required = isSpecificTimeFrameVisible && isSpotRatesChecked === "Y";
                this.textboxSpecificTimeframeContract.visible = isSpecificTimeFrameVisible && isContractRatesChecked === "Y";
                this.textboxSpecificTimeframeContract.required = isSpecificTimeFrameVisible && isContractRatesChecked === "Y";
                this.textboxSpecificAreaType.visible = isSpecificAreaTypeVisible;
                this.textboxSpecificAreaType.required = isSpecificAreaTypeVisible;
            }
        }
    }

    private updateEscalationComboValues() {
        const isSpotChecked = this.switchSpotRates.checked;
        const isContractChecked = this.switchContractRates.checked;

        this.textboxSpecificTimeFrame.items = this._timeFrameSpotOptions;
        this.textboxSpecificTimeFrame.selectedItem = this._timeFrameSpotOptions[0];
        this.textboxSpecificTimeframeContract.items = this._timeFrameContractOptions;
        this.textboxSpecificTimeframeContract.selectedItem = this._timeFrameContractOptions[0];
    }

    buttonDeleteOnClick(event: ClickEvent) {
        const tableRow = this.tableRateViewUsers.findTableRowFromAdditionalTool(event.target as Button);
        const modelRow = tableRow.data;
        CommonDialogs.showYesNo("Are you sure you want to delete this record?", "Confirm Deletion", {
            xVisible: true
        }, false).then(async clicked => {
            if (clicked) {
                Api.post("lme/general/update/service-type", {
                    "called_from": "R",
                    "user_id": modelRow.get("user_id")
                }).then(result => {
                    if (result?.data[0]?.success) {
                        this.tableRateViewUsers.removeRow(modelRow);
                    } else {
                        Snackbar.showWarningSnackbar("Unable to delete user.");
                    }
                });
            }
        });
    }

    textboxDatUserIdOnChange(event: ChangeEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data;
        const isExistingRow = !modelRow._appending;

        if (event.userInitiatedChange && isExistingRow) {
            const buttonDatRestUserAuthorize = tableRow.findComponentById("buttonAuthorizeUser") as Button;
            const labelDatStatus = tableRow.findComponentById("labelRateViewUserStatus") as Label;

            buttonDatRestUserAuthorize.visible = true;
            labelDatStatus.visible = false;
        }
    }

    buttonAuthorizeUserOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const textboxUserId = tableRow.findComponentById("textboxDatRateviewUserId") as Textbox;
        const textboxDatUserId = tableRow.findComponentById("textboxDatUserId") as Textbox;

        Api.post("lme/general/rateindex/rateview/auth", {
            "action_type": "UL",
            "user_id": textboxUserId.text,
            "dat_rateview_user": textboxDatUserId.text
        }).then(response => {
            if (response?.data[0]?.is_valid_token) {
                tableRow.data.set("is_valid_token", true);
                this.setUserAuthSuccess(tableRow)
            } else {
                this.setUserAuthFailed(tableRow, response?.data[0].dat_user_auth_error);
                tableRow.data.set("is_valid_token", false);
                if (StringUtil.equalsIgnoreCase(response.data[0].dat_user_auth_error, "Authentication is blocked for one minute.")) {
                    Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
                }
            }
        });
    }

    textboxDatRateviewUserIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_rateview = true;
    }

    private hasValidFieldsForDatRest(activeRow: ModelRow) {
        const isRateViewEnabled = activeRow?.get("dat_rateview_enabled") === true || activeRow?.data?.dat_default_values?.dat_rateview_enabled === true;

        return isRateViewEnabled && (!StringUtil.isEmptyString(activeRow?.get("dat_account_id")) &&
            !StringUtil.isEmptyString(activeRow?.get("dat_password")) &&
            !StringUtil.isEmptyString(activeRow?.get("rateview_auth_url")));
    }

    private promptUserToUpdateCredentials(): any {
        this.promptTriggered = true;
        try {
            const dialogLayout = Layout.getLayout("lme/general/UpdateCredentialsDialog") as UpdateCredentialsDialog;
            CommonDialogs.showYesNo(dialogLayout, "Edit Account ID & Password", {
                yesButtonCaption: "Yes",
                noButtonCaption: "No",
                xVisible: true,
            }, true, 100).then(clicked => {
                if (clicked) {
                    this.buttonOrganizationAuth.visible = true;
                    this.buttonOrganizationAuth.enabled = true;
                    this.labelAuthStatus.visible = false;
                    this.setupButtonOrganizationAuthTooltip();
                    this.shouldUpdateLoadboardCredentials = true;
                    return true;
                } else {
                    // Reset the credentials to original values
                    this.mainDataSource.activeRow.set("dat_password", this.originalDatPassword);
                    this.mainDataSource.activeRow.set("dat_account_id", this.originalDatAccountId);
                    this.shouldUpdateLoadboardCredentials = false;
                    return false;
                }
            });
        }catch (error) {
            Snackbar.showWarningSnackbar("Failed to open update credentials dialog:", error);
            console.error("Failed to open update credentials dialog:", error);
        }
    }
}
